import React, { useMemo, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

/**
 * Two-level dropdown: Category → Group (type).
 * Same UX as triple dropdown but only 2 columns; selection is (category, group), not a single item id.
 * @param {Object} grouped - { category: { type: [items] } }
 * @param {{ category: string, group: string }} value - { category, group }
 * @param {function(category: string, group: string)} onChange - called when user clicks a group
 * @param {string} [placeholder]
 * @param {string} [label]
 * @param {string} [buttonClassName]
 */
const AirItemTypeCategoryGroupDropdown = ({
  grouped = {},
  value = { category: '', group: '' },
  onChange,
  placeholder = 'เลือกหมวดหมู่ > กลุ่ม',
  label,
  buttonClassName = 'w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);

  const categories = useMemo(() => Object.keys(grouped).sort(), [grouped]);
  const groupsInOpenCategory = useMemo(() => {
    if (!openCategory || !grouped[openCategory]) return [];
    return Object.keys(grouped[openCategory]).sort();
  }, [grouped, openCategory]);

  const displayLabel = value?.category && value?.group
    ? `${value.category} > ${value.group}`
    : '';

  const handleSelectGroup = (category, group) => {
    onChange(category, group);
    setDropdownOpen(false);
    setOpenCategory(null);
  };

  const handleClose = () => {
    setDropdownOpen(false);
    setOpenCategory(null);
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          className={buttonClassName}
        >
          <span className={displayLabel ? 'text-gray-900' : 'text-gray-500'}>
            {displayLabel || placeholder}
          </span>
          <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
        </button>
        {dropdownOpen && (
          <>
            <div className="fixed inset-0 z-10" aria-hidden="true" onClick={handleClose} />
            <div
              className="absolute z-20 mt-1 flex rounded-md border border-gray-200 bg-white shadow-lg"
              style={{ minWidth: 320 }}
            >
              <ul className="py-1 border-r border-gray-100 min-w-[140px] max-h-[300px] overflow-y-auto">
                {categories.map((cat) => (
                  <li
                    key={cat}
                    onMouseEnter={() => setOpenCategory(cat)}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenCategory(openCategory === cat ? null : cat)}
                      className={`block w-full text-left px-3 py-2 text-sm cursor-pointer whitespace-nowrap ${
                        openCategory === cat ? 'bg-indigo-100 text-indigo-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {cat}
                    </button>
                  </li>
                ))}
              </ul>
              {openCategory && grouped[openCategory] && (
                <ul className="py-1 min-w-[140px] max-h-[300px] overflow-y-auto">
                  {groupsInOpenCategory.map((group) => (
                    <li key={group}>
                      <button
                        type="button"
                        onClick={() => handleSelectGroup(openCategory, group)}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-800 whitespace-nowrap"
                      >
                        {group}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AirItemTypeCategoryGroupDropdown;
